<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  getDefaultImageForTemplate,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );

  // Auto-populate default image for known templates
  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    const defaultImage = getDefaultImageForTemplate(props.template.name);
    if (defaultImage) {
      processedParams.value.header ??= {};
      processedParams.value.header.media_url = `${window.location.origin}${defaultImage}`;
    }
  }

  // Auto-populate contact's first name variable if available
  const contact = props.contact || {};
  let firstName = contact.first_name || contact.firstName || '';
  if (!firstName && contact.name) {
    firstName = contact.name.trim().split(/\s+/)[0] || '';
  }

  if (processedParams.value.body && firstName) {
    // Standard WhatsApp templates usually use "1" for the first parameter (often the contact's name)
    if ('1' in processedParams.value.body && !processedParams.value.body['1']) {
      processedParams.value.body['1'] = firstName;
    }

    // Also handle named variables (case-insensitive check for first_name, firstname, or name)
    Object.keys(processedParams.value.body).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (
        (lowerKey.includes('first_name') ||
          lowerKey.includes('firstname') ||
          lowerKey.includes('name')) &&
        !processedParams.value.body[key]
      ) {
        processedParams.value.body[key] = firstName;
      }
    });
  }

  if (processedParams.value.header?.media_url) {
    mediaSourceType.value = 'url';
    isUrlDirectlyEntered.value = true;
  } else {
    mediaSourceType.value = 'upload';
    isUrlDirectlyEntered.value = false;
  }
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const accountId = useMapGetter('getCurrentAccountId');

const mediaSourceType = ref('upload'); // 'upload' | 'url'
const isUploading = ref(false);
const uploadError = ref('');
const uploadedFileName = ref('');
const isUrlDirectlyEntered = ref(false);
const dragOver = ref(false);
const fileInputRef = ref(null);

const setMediaSourceType = type => {
  mediaSourceType.value = type;
};

const handleDirectUrlUpdate = value => {
  isUrlDirectlyEntered.value = true;
  updateMediaUrl(value);
};

const clearUploadedFile = () => {
  updateMediaUrl('');
  uploadedFileName.value = '';
  isUrlDirectlyEntered.value = false;
  uploadError.value = '';
};

const triggerFileSelect = () => {
  fileInputRef.value?.click();
};

const processFile = async file => {
  if (!file) return;

  const maxSizes = {
    IMAGE: 5 * 1024 * 1024,      // 5MB
    VIDEO: 16 * 1024 * 1024,     // 16MB
    DOCUMENT: 100 * 1024 * 1024, // 100MB
  };

  const format = headerComponent.value?.format?.toUpperCase() || 'IMAGE';
  const maxSize = maxSizes[format] || maxSizes.IMAGE;

  if (file.size > maxSize) {
    const sizeInMB = Math.round(maxSize / (1024 * 1024));
    const errorMsg = t('WHATSAPP_TEMPLATES.PARSER.FILE_TOO_LARGE', { size: sizeInMB }) || `File size exceeds the limit of ${sizeInMB}MB.`;
    uploadError.value = errorMsg;
    useAlert(errorMsg);
    return;
  }

  isUploading.value = true;
  uploadError.value = '';

  try {
    const response = await uploadFile(file, accountId.value);
    if (response && response.fileUrl) {
      updateMediaUrl(response.fileUrl);
      uploadedFileName.value = file.name;
      isUrlDirectlyEntered.value = false;
    } else {
      throw new Error('Upload failed');
    }
  } catch (error) {
    const errorMsg = t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_ERROR') || 'Failed to upload file. Please try again.';
    uploadError.value = errorMsg;
    useAlert(errorMsg);
  } finally {
    isUploading.value = false;
  }
};

const handleFileChange = event => {
  const file = event.target.files[0];
  processFile(file);
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const handleFileDrop = event => {
  dragOver.value = false;
  const file = event.dataTransfer?.files[0];
  processFile(file);
};

const mediaIcon = computed(() => {
  const format = headerComponent.value?.format?.toUpperCase();
  if (format === 'IMAGE') return 'i-lucide-image';
  if (format === 'VIDEO') return 'i-lucide-video';
  if (format === 'DOCUMENT') return 'i-lucide-file-text';
  return 'i-lucide-file';
});

const fallbackFileName = computed(() => {
  const format = headerComponent.value?.format?.toUpperCase();
  return `${formatType.value || 'Media'} File`;
});

const allowedFileTypes = computed(() => {
  const format = headerComponent.value?.format?.toUpperCase();
  if (format === 'IMAGE') return 'image/png, image/jpeg, image/jpg, image/webp, image/gif';
  if (format === 'VIDEO') return 'video/mp4, video/3gpp';
  if (format === 'DOCUMENT') return '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
  return '*/*';
});

const allowedFileTypesHelpText = computed(() => {
  const format = headerComponent.value?.format?.toUpperCase();
  if (format === 'IMAGE') return 'PNG, JPG, JPEG, WEBP or GIF (Max 5MB)';
  if (format === 'VIDEO') return 'MP4, 3GP (Max 16MB)';
  if (format === 'DOCUMENT') return 'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX or TXT (Max 100MB)';
  return 'Max 5MB';
});

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

watch(
  () => props.contact,
  (newContact) => {
    if (newContact && processedParams.value.body) {
      let firstName = newContact.first_name || newContact.firstName || '';
      if (!firstName && newContact.name) {
        firstName = newContact.name.trim().split(/\s+/)[0] || '';
      }
      if (firstName) {
        if ('1' in processedParams.value.body && !processedParams.value.body['1']) {
          processedParams.value.body['1'] = firstName;
        }
        Object.keys(processedParams.value.body).forEach(key => {
          const lowerKey = key.toLowerCase();
          if (
            (lowerKey.includes('first_name') ||
              lowerKey.includes('firstname') ||
              lowerKey.includes('name')) &&
            !processedParams.value.body[key]
          ) {
            processedParams.value.body[key] = firstName;
          }
        });
      }
    }
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>

        <!-- Segmented Toggle to choose source type -->
        <div class="flex p-0.5 mb-3 rounded-lg bg-n-alpha-black2 w-fit">
          <button
            type="button"
            class="py-1 px-3 text-xs font-medium rounded-md transition-all cursor-pointer"
            :class="mediaSourceType === 'upload' ? 'bg-n-solid-3 dark:bg-n-solid-2 text-n-slate-12 shadow-sm' : 'text-n-slate-11 hover:text-n-slate-12'"
            @click="setMediaSourceType('upload')"
          >
            {{ t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_FILE_TAB') || 'Upload File' }}
          </button>
          <button
            type="button"
            class="py-1 px-3 text-xs font-medium rounded-md transition-all cursor-pointer"
            :class="mediaSourceType === 'url' ? 'bg-n-solid-3 dark:bg-n-solid-2 text-n-slate-12 shadow-sm' : 'text-n-slate-11 hover:text-n-slate-12'"
            @click="setMediaSourceType('url')"
          >
            {{ t('WHATSAPP_TEMPLATES.PARSER.PASTE_URL_TAB') || 'Paste URL' }}
          </button>
        </div>

        <!-- Upload Source UI -->
        <div v-if="mediaSourceType === 'upload'" class="mb-2.5">
          <!-- Uploading state -->
          <div
            v-if="isUploading"
            class="flex flex-col items-center justify-center p-6 border border-dashed rounded-xl border-n-slate-6 dark:border-n-slate-7 bg-n-alpha-black2"
          >
            <Spinner class="w-8 h-8 text-n-brand mb-2" />
            <p class="text-xs text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATES.PARSER.UPLOADING') || 'Uploading file...' }}
            </p>
          </div>

          <!-- Uploaded file state -->
          <div
            v-else-if="processedParams.header?.media_url && !isUrlDirectlyEntered"
            class="flex items-center justify-between p-3 border rounded-xl border-n-slate-6 dark:border-n-slate-7 bg-n-alpha-black2"
          >
            <div class="flex items-center gap-3 min-w-0">
              <div
                class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-slate-3 dark:bg-n-slate-8 text-n-slate-11"
              >
                <Icon :icon="mediaIcon" class="text-xl" />
              </div>
              <div class="flex flex-col min-w-0">
                <p class="m-0 text-sm font-medium text-n-slate-12 truncate">
                  {{ uploadedFileName || fallbackFileName }}
                </p>
                <a
                  :href="processedParams.header.media_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs text-n-brand hover:underline truncate"
                >
                  {{ t('WHATSAPP_TEMPLATES.PARSER.VIEW_FILE') || 'View uploaded file' }}
                </a>
              </div>
            </div>
            <button
              type="button"
              class="p-2 transition-colors rounded-lg hover:bg-n-slate-3 dark:hover:bg-n-slate-8 text-n-slate-11 hover:text-n-ruby-9 cursor-pointer"
              @click="clearUploadedFile"
            >
              <Icon icon="i-lucide-trash" class="text-base" />
            </button>
          </div>

          <!-- Drag and drop zone -->
          <div
            v-else
            class="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300"
            :class="[
              dragOver
                ? 'border-n-brand bg-n-brand/5 dark:bg-n-brand/10'
                : 'border-n-slate-6 dark:border-n-slate-7 hover:bg-n-alpha-black2 hover:border-n-slate-8'
            ]"
            @click="triggerFileSelect"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="handleFileDrop"
          >
            <Icon icon="i-lucide-cloud-upload" class="text-3xl text-n-slate-10 mb-2" />
            <p class="text-sm font-medium text-n-slate-12 text-center">
              {{ t('WHATSAPP_TEMPLATES.PARSER.DRAG_DROP_OR') || 'Drag and drop file here, or' }}
              <span class="text-n-brand hover:underline font-semibold">
                {{ t('WHATSAPP_TEMPLATES.PARSER.SELECT_FILE') || 'select file' }}
              </span>
            </p>
            <p class="text-xs text-n-slate-10 mt-1 text-center">
              {{ allowedFileTypesHelpText }}
            </p>
            <input
              ref="fileInputRef"
              type="file"
              class="hidden"
              :accept="allowedFileTypes"
              @change="handleFileChange"
            />
          </div>

          <p v-if="uploadError" class="text-xs text-n-ruby-9 mt-2">
            {{ uploadError }}
          </p>
        </div>

        <!-- URL Source UI -->
        <div v-else class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="handleDirectUrlUpdate"
          />
        </div>

        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
